import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Lock } from "lucide-react";

export default function BillingBanner() {
  const { user } = useAuth();
  const loc = useLocation();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!user || user === false || user.role !== "seller") return;
    (async () => {
      try {
        const { data } = await api.get("/billing/status");
        setState(data);
      } catch { /* ignore */ }
    })();
  }, [user, loc.pathname]);

  if (!user || user === false || user.role !== "seller") return null;
  if (!state || state.is_active === undefined) return null;
  if (loc.pathname.startsWith("/billing")) return null;

  const isTrial = state.status === "trialing";
  const isPastDue = !state.is_active;
  const annualUpsell = state.is_active && state.plan !== "annual" && state.last_payment_at &&
    state.grace_days_left != null && state.grace_days_left <= 14;

  if (!isTrial && !isPastDue && !annualUpsell) return null;

  if (annualUpsell && !isTrial && !isPastDue) {
    const saving = (state.monthly_fee_usd * 12 - (state.annual_fee_usd ?? 1000)).toFixed(0);
    return (
      <div className="border-b bg-emerald-500/10 border-emerald-500/40 text-emerald-400" data-testid="annual-upsell-banner">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-between gap-4 text-sm">
          <span>
            Renewal in <span className="font-mono">{state.grace_days_left}</span> day{state.grace_days_left === 1 ? "" : "s"} — switch to annual
            (<span className="font-mono">${(state.annual_fee_usd ?? 1000).toFixed(0)}</span>/yr) and save <span className="font-mono font-bold">${saving}</span>.
          </span>
          <Link to="/billing" className="underline underline-offset-2 font-medium whitespace-nowrap" data-testid="annual-upsell-link">
            Switch to annual
          </Link>
        </div>
      </div>
    );
  }

  const bg = isPastDue
    ? "bg-[#FF3B30]/10 border-[#FF3B30]/40 text-[#FF3B30]"
    : "bg-[#007AFF]/10 border-[#007AFF]/40 text-[#007AFF]";

  return (
    <div className={`border-b ${bg}`} data-testid="billing-banner">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-between gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Lock className="h-3.5 w-3.5" />
          {isPastDue ? (
            <span>Monthly access fee (<span className="font-mono">${state.monthly_fee_usd.toFixed(0)}</span>) is due. Publishing, orders and AI are paused.</span>
          ) : (
            <span>
              You&apos;re on a free trial. <span className="font-mono">{state.grace_days_left}</span> day{state.grace_days_left === 1 ? "" : "s"} left before the <span className="font-mono">${state.monthly_fee_usd.toFixed(0)}</span>/month access fee starts.
            </span>
          )}
        </div>
        <Link to="/billing" className="underline underline-offset-2 font-medium whitespace-nowrap" data-testid="billing-banner-link">
          {isPastDue ? "Pay now" : "Manage billing"}
        </Link>
      </div>
    </div>
  );
}
